// src/data/stories/buildStories.ts
//
// Derives the Stories feed from EXISTING content — no new data tables.
//   • Highlights / partner / navigational bubbles  (pinned, mirror the design ref)
//   • Content stories  ← src/data/mock/news.ts      (gallery → slides)
//   • "Our Journey"    ← src/data/mock/achievements.ts (extends range back to 2015)
//
// Presentation-only fields (durations, backgrounds, animation config, badges)
// live here as static config, since the source models don't carry them.

import { news } from '@/data/mock/news';
import { achievements } from '@/data/mock/achievements';
import { activities } from '@/data/mock/activities';
import { martyrsPlayers } from '@/data/mock/martyrsPlayers';
import type {
  Story,
  StorySlide,
  StoryTextBlock,
  TextAnimation,
  TextStyle,
  BilingualText,
} from './types';

// ---------------------------------------------------------------------------
// Animation presets per text style
// ---------------------------------------------------------------------------
const ANIM: Record<TextStyle, TextAnimation> = {
  headline: { mode: 'word_by_word', delay: 200, speed: 90, easing: 'ease-out' },
  subhead: { mode: 'line_by_line', delay: 350, speed: 320, easing: 'ease-out' },
  body: { mode: 'line_by_line', delay: 300, speed: 280, easing: 'ease-out' },
  caption: { mode: 'fade', delay: 200, speed: 200, easing: 'ease-out' },
};

let blockSeq = 0;
const text = (
  style: TextStyle,
  content: BilingualText,
  override: Partial<TextAnimation> = {},
): StoryTextBlock => ({
  id: `tb-${blockSeq++}`,
  content,
  style,
  animation: { ...ANIM[style], ...override },
});

const BG = {
  navy: 'linear-gradient(160deg,#0b1f3a 0%,#070138 100%)',
  violet: 'linear-gradient(160deg,#1a1140 0%,#070138 100%)',
  gold: 'linear-gradient(160deg,#5a3b0a 0%,#241803 100%)',
  teal: 'linear-gradient(160deg,#08313a 0%,#061826 100%)',
};

const READ_MORE: BilingualText = { en: 'Read more', ar: 'اقرأ المزيد' };

// ---------------------------------------------------------------------------
// Year-grouped content stories (one bubble per YEAR, newest → oldest)
//    Each year's slides = that year's news events + achievements.
// ---------------------------------------------------------------------------
const YEAR_BACKGROUNDS = [BG.navy, BG.violet, BG.teal, BG.gold];

// Per-year photo galleries (from the academy archive slides). Number of photos
// available per year under /public/assets/stories/<year>/NN.webp (1-indexed).
const YEAR_GALLERY_COUNTS: Record<number, number> = {
  2015: 15,
  2016: 13,
  2017: 24,
  2018: 12,
};

/** Build ordered photo URLs for a year's gallery, e.g. /assets/stories/2015/01.webp */
function galleryUrls(year: number): string[] {
  const n = YEAR_GALLERY_COUNTS[year] ?? 0;
  return Array.from(
    { length: n },
    (_, i) => `/assets/stories/${year}/${String(i + 1).padStart(2, '0')}.webp`,
  );
}

function clamp(s: string, n: number): string {
  return s.length > n ? s.slice(0, n).trimEnd() + '…' : s;
}

const AR_DIGITS = '٠١٢٣٤٥٦٧٨٩';
const toArabicDigits = (s: string) => s.replace(/[0-9]/g, (d) => AR_DIGITS[+d]);

function fmtDate(iso: string): BilingualText {
  const d = new Date(iso);
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
  return {
    en: d.toLocaleDateString('en-US', opts),
    ar: d.toLocaleDateString('ar-EG', opts),
  };
}

function buildYearStories(): Story[] {
  const byYear = new Map<
    number,
    { news: typeof news; ach: typeof achievements; act: typeof activities }
  >();
  const bucket = (y: number) => {
    if (!byYear.has(y)) byYear.set(y, { news: [], ach: [], act: [] });
    return byYear.get(y)!;
  };
  news.forEach((n) => bucket(new Date(n.date).getFullYear()).news.push(n));
  activities.forEach((a) => bucket(new Date(a.date).getFullYear()).act.push(a));
  achievements.forEach((a) => bucket(a.year).ach.push(a));

  const years = [...byYear.keys()].sort((a, b) => b - a); // newest → oldest

  return years.map((year) => {
    const { news: yn, ach: ya, act: yact } = byYear.get(year)!;
    const newsSorted = [...yn].sort((a, b) => +new Date(b.date) - +new Date(a.date));
    const actSorted = [...yact].sort((a, b) => +new Date(b.date) - +new Date(a.date));
    const slides: StorySlide[] = [];
    let sort = 1;

    // archive photo gallery of this year — one slide per photo, shown first.
    const gallery = galleryUrls(year);
    const yearLabel: BilingualText = { en: String(year), ar: toArabicDigits(String(year)) };
    gallery.forEach((url, i) => {
      slides.push({
        id: `y${year}-photo-${i + 1}`,
        sort: sort++,
        duration: 3500,
        media: [{ type: 'image', url, fit: 'cover' }],
        // only the first photo carries the year caption — keep the rest clean.
        textBlocks: i === 0 ? [text('caption', yearLabel)] : [],
      });
    });

    // news events of this year — one slide each (cover + date + title + excerpt)
    for (const item of newsSorted) {
      slides.push({
        id: `y${year}-${item.slug}`,
        sort: sort++,
        duration: 5500,
        media: [{ type: 'image', url: item.image, fit: 'cover' }],
        textBlocks: [
          text('caption', fmtDate(item.date)),
          text('headline', item.title),
          text('subhead', { en: clamp(item.excerpt.en, 130), ar: clamp(item.excerpt.ar, 130) }),
        ],
        cta: { label: READ_MORE, url: `/news/${item.slug}` },
      });
    }

    // activities/events of this year — one slide each
    for (const item of actSorted) {
      slides.push({
        id: `y${year}-act-${item.slug}`,
        sort: sort++,
        duration: 5500,
        media: [{ type: 'image', url: item.image, fit: 'cover' }],
        textBlocks: [
          text('caption', fmtDate(item.date)),
          text('headline', item.title),
          text('subhead', { en: clamp(item.description.en, 130), ar: clamp(item.description.ar, 130) }),
        ],
        cta: { label: { en: 'View activities', ar: 'عرض الأنشطة' }, url: '/activities' },
      });
    }

    // achievements of this year — text-on-gradient slides
    ya.forEach((a, i) => {
      slides.push({
        id: `y${year}-${a.id}`,
        sort: sort++,
        duration: 4500,
        background: YEAR_BACKGROUNDS[i % YEAR_BACKGROUNDS.length],
        media: [],
        textBlocks: [
          text('caption', { en: String(year), ar: toArabicDigits(String(year)) }),
          text('headline', a.title),
          text('body', { en: clamp(a.description.en, 170), ar: clamp(a.description.ar, 170) }),
        ],
      });
    });

    // keep every gallery photo, plus a handful of news/achievement slides.
    const cap = Math.max(14, gallery.length + 6);
    const capped = slides.slice(0, cap);
    const coverImg = gallery[0] ?? newsSorted[0]?.image ?? actSorted[0]?.image;
    const cover = coverImg
      ? ({ type: 'image', value: coverImg } as const)
      : ({ type: 'icon', value: 'trophy' } as const);

    return {
      id: `year-${year}`,
      label: { en: String(year), ar: toArabicDigits(String(year)) },
      cover,
      badge: null,
      publishedAt: `${year}-12-31`,
      pinned: false,
      hideHeaderDate: true,
      slides: capped,
    } satisfies Story;
  });
}

// ---------------------------------------------------------------------------
// 3) Forward-looking year stories (2026, 2025) — no dated events exist yet,
//    so these are curated, evergreen "what's ahead" stories with real photos.
// ---------------------------------------------------------------------------
function buildFutureYearStories(): Story[] {
  const yearLabel = (y: number) => ({ en: String(y), ar: toArabicDigits(String(y)) });

  const story2026: Story = {
    id: 'year-2026',
    label: yearLabel(2026),
    cover: { type: 'image', value: '/img/sports/football.jpg' },
    badge: null,
    publishedAt: '2026-12-31',
    pinned: false,
    hideHeaderDate: true,
    slides: [
      {
        id: 'y2026-season',
        sort: 1,
        duration: 5500,
        media: [{ type: 'image', url: '/img/sports/football.jpg', fit: 'cover' }],
        textBlocks: [
          text('caption', yearLabel(2026)),
          text('headline', { en: 'A New Season Begins', ar: 'موسم جديد ينطلق' }),
          text('subhead', {
            en: 'Champions Academy keeps growing across Gaza — registration is open.',
            ar: 'تواصل أكاديمية تشامبيونز نموّها في غزة — التسجيل مفتوح.',
          }),
        ],
        cta: { label: { en: 'Register now', ar: 'سجّل الآن' }, url: '/booking' },
      },
      {
        id: 'y2026-camps',
        sort: 2,
        duration: 5500,
        media: [{ type: 'image', url: '/assets/activities/real-madrid-2023.jpg', fit: 'cover' }],
        textBlocks: [
          text('headline', { en: 'World-Class Camps Continue', ar: 'معسكرات عالمية مستمرة' }),
          text('body', {
            en: 'Methodology and camps with our global partners carry into 2026.',
            ar: 'تتواصل المنهجيات والمعسكرات مع شركائنا الدوليين خلال 2026.',
          }),
        ],
        cta: { label: { en: 'Explore', ar: 'استكشف' }, url: '/initiative' },
      },
      {
        id: 'y2026-workshops',
        sort: 3,
        duration: 5500,
        media: [{ type: 'image', url: '/assets/activities/coaches-workshop-2024.jpg', fit: 'cover' }],
        textBlocks: [
          text('headline', { en: 'Workshops & Webinars', ar: 'ورش وندوات' }),
          text('body', {
            en: 'New certified sessions for coaches and players.',
            ar: 'جلسات معتمدة جديدة للمدربين واللاعبين.',
          }),
        ],
        cta: { label: { en: 'Explore', ar: 'استكشف' }, url: '/initiative' },
      },
    ],
  };

  const story2025: Story = {
    id: 'year-2025',
    label: yearLabel(2025),
    cover: { type: 'image', value: '/assets/activities/champions-cup-2023.jpg' },
    badge: null,
    publishedAt: '2025-12-31',
    pinned: false,
    hideHeaderDate: true,
    slides: [
      {
        id: 'y2025-wins',
        sort: 1,
        duration: 5500,
        media: [{ type: 'image', url: '/assets/activities/champions-cup-2023.jpg', fit: 'cover' }],
        textBlocks: [
          text('caption', yearLabel(2025)),
          text('headline', { en: 'Building on Our Wins', ar: 'نبني على إنجازاتنا' }),
          text('subhead', {
            en: 'More titles, more talents representing Palestine.',
            ar: 'المزيد من الألقاب والمواهب التي تمثّل فلسطين.',
          }),
        ],
        cta: { label: { en: 'Achievements', ar: 'الإنجازات' }, url: '/achievements' },
      },
      {
        id: 'y2025-sports',
        sort: 2,
        duration: 5500,
        media: [{ type: 'image', url: '/img/sports/basketball.jpg', fit: 'cover' }],
        textBlocks: [
          text('headline', { en: 'A Program for Every Sport', ar: 'برنامج لكل رياضة' }),
          text('body', {
            en: 'Football, basketball, karate, swimming, table tennis and more.',
            ar: 'كرة القدم، السلة، الكاراتيه، السباحة، تنس الطاولة وغيرها.',
          }),
        ],
        cta: { label: { en: 'Our sports', ar: 'رياضاتنا' }, url: '/sports' },
      },
      {
        id: 'y2025-partners',
        sort: 3,
        duration: 5500,
        media: [{ type: 'image', url: '/assets/activities/coaches-workshop-2024.jpg', fit: 'cover' }],
        textBlocks: [
          text('headline', { en: 'Global Partnerships', ar: 'شراكات عالمية' }),
          text('body', {
            en: 'Real Madrid, Barça Innovation, Ekkono and more.',
            ar: 'ريال مدريد، برشلونة إنوفيشن، إيكونو وغيرها.',
          }),
        ],
        cta: { label: { en: 'Explore', ar: 'استكشف' }, url: '/initiative' },
      },
    ],
  };

  return [story2026, story2025];
}

// ---------------------------------------------------------------------------
// "The Martyrs" — a pinned memorial story honoring the academy members,
//    players and staff who were martyred. Names come from the memorial list
//    in src/data/mock/martyrsPlayers.ts; rendered as somber text-on-gradient
//    slides (no fabricated photos), linking through to the War Impact page.
// ---------------------------------------------------------------------------
const MEMORIAL_BG = 'linear-gradient(160deg,#16233d 0%,#070138 100%)';

// TEMP: placeholder photos so the memorial story can be previewed with images.
// Swap these for real martyr portraits once available.
const MARTYRS_PLACEHOLDER_PHOTOS = [
  '/assets/activities/champions-cup-2023.jpg',
  '/assets/activities/ramadan-tournament-2023.jpg',
  '/assets/activities/summer-camp-2023.jpg',
  '/assets/activities/sports-day-2024.jpg',
  '/assets/activities/westbank-tour-2023.jpg',
  '/assets/stories/2015/01.webp',
  '/assets/stories/2015/05.webp',
];
const photoAt = (i: number) => MARTYRS_PLACEHOLDER_PHOTOS[i % MARTYRS_PLACEHOLDER_PHOTOS.length];

/** Split an array into chunks of at most `size`. */
function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function buildMartyrsStory(): Story {
  const memorialCta: BilingualText = { en: 'Honor their memory', ar: 'تعرّف عليهم' };
  const CTA_URL = '/war-impact#players';

  const staff = martyrsPlayers.filter((p) => p.sport.en === 'Staff');
  const members = martyrsPlayers.filter((p) => p.sport.en !== 'Staff');

  const nameList = (people: typeof martyrsPlayers): BilingualText => ({
    en: people.map((p) => p.name.en).join(' • '),
    ar: people.map((p) => p.name.ar).join(' • '),
  });

  const slides: StorySlide[] = [];
  let sort = 1;

  // 1) Somber intro
  slides.push({
    id: 'martyrs-intro',
    sort: sort++,
    duration: 6500,
    background: MEMORIAL_BG,
    media: [{ type: 'image', url: photoAt(0), fit: 'cover' }],
    textBlocks: [
      text('caption', { en: 'In their memory', ar: 'في ذكراهم' }),
      text('headline', { en: 'The Martyrs', ar: 'الشهداء' }),
      text('subhead', {
        en: 'We honor the players, members and staff of our academy who were martyred. Their spirit stays on our fields.',
        ar: 'نُخلّد ذكرى لاعبي وأعضاء وطاقم أكاديميتنا الذين ارتقوا شهداء. أرواحهم تبقى حاضرة في ملاعبنا.',
      }),
    ],
    cta: { label: memorialCta, url: CTA_URL },
  });

  // 2) Staff & coaches
  if (staff.length) {
    slides.push({
      id: 'martyrs-staff',
      sort: sort++,
      duration: 6500,
      background: MEMORIAL_BG,
      media: [{ type: 'image', url: photoAt(1), fit: 'cover' }],
      textBlocks: [
        text('caption', { en: 'Staff & Coaches', ar: 'الطاقم والمدربون' }),
        text('body', nameList(staff)),
      ],
      cta: { label: memorialCta, url: CTA_URL },
    });
  }

  // 3) Players & members — chunked so each slide stays readable
  chunk(members, 6).forEach((group, i) => {
    slides.push({
      id: `martyrs-members-${i + 1}`,
      sort: sort++,
      duration: 6500,
      background: MEMORIAL_BG,
      media: [{ type: 'image', url: photoAt(i + 2), fit: 'cover' }],
      textBlocks: [
        text('caption', { en: 'Players & Members', ar: 'اللاعبون والأعضاء' }),
        text('body', nameList(group)),
      ],
      cta: { label: memorialCta, url: CTA_URL },
    });
  });

  // 4) Closing
  slides.push({
    id: 'martyrs-closing',
    sort: sort++,
    duration: 5500,
    background: MEMORIAL_BG,
    media: [{ type: 'image', url: photoAt(6), fit: 'cover' }],
    textBlocks: [
      text('headline', { en: 'We will not forget them', ar: 'لن ننساهم' }),
      text('subhead', { en: 'Forever part of the Champions family.', ar: 'يبقون دائمًا جزءًا من عائلة تشامبيونز.' }),
    ],
    cta: { label: memorialCta, url: CTA_URL },
  });

  return {
    id: 'martyrs',
    label: { en: 'The Martyrs', ar: 'الشهداء' },
    cover: { type: 'icon', value: 'candle' },
    badge: null,
    publishedAt: '2099-01-01', // keep ahead of dated content; pinned anyway
    pinned: true,
    hideHeaderDate: true,
    slides,
  } satisfies Story;
}

// ---------------------------------------------------------------------------
// Public builder — final display order
// ---------------------------------------------------------------------------
export function buildStories(): Story[] {
  blockSeq = 0;
  // Pinned memorial first, then years newest → oldest.
  const martyrs = buildMartyrsStory();
  const future = buildFutureYearStories(); // 2026, 2025 (curated)
  const years = buildYearStories(); // 2024 → 2015 (from data)
  return [martyrs, ...future, ...years];
}
